import React, { useEffect, useRef, useState } from "react";
import { Download, Palette, Loader2, Activity, BarChart3, TrendingUp, Eye, Heart, Rocket, RefreshCw, Code2, Copy, Check, ExternalLink, Radio } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import NowSpinning from "@/components/NowSpinning";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

/**
 * MarketingKit — branded press-kit tiles ready to download for investor
 * decks, app-store listings, partnership pitches, and social bios.
 * Uses html-to-image to client-side render any tile into a PNG.
 *
 * LIVE tiles embed real-time analytics panels (KPIs, growth chart, social
 * performance) pulled from /api/news/analytics so the press visuals are
 * always backed by current station metrics.
 */
const TILES = [
  {
    id: "og-1200x630",
    label: "Open Graph · 1200×630",
    use: "Twitter / X / LinkedIn / FB link preview",
    w: 1200, h: 630,
    headline: "Hands-free broadcast automation.",
    sub: "One platform. Every news bulletin, social post, blog & podcast — for your radio station.",
  },
  {
    id: "ig-square-1080",
    label: "Instagram Feed · 1080×1080",
    use: "IG · FB feed",
    w: 1080, h: 1080,
    headline: "Your AI Newsroom on autopilot.",
    sub: "Ingest. Generate. Voice. Mix. Render. Schedule. Publish.",
  },
  {
    id: "ig-story-1080x1920",
    label: "Story / Reel · 1080×1920",
    use: "IG Stories · TikTok · YT Shorts cover",
    w: 1080, h: 1920,
    headline: "1 story → 9 outputs in 22 seconds.",
    sub: "Audio · Reels · Shorts · Blog · 5 social posts — auto.",
  },
  {
    id: "appstore-1200x800",
    label: "App Store Hero · 1200×800",
    use: "Investor decks · partnership pitches",
    w: 1200, h: 800,
    headline: "The Complete AI OS for Radio Stations.",
    sub: "WordPress · IG · FB · X · TikTok · YouTube · WhatsApp",
  },
];

const LIVE_TILES = [
  {
    id: "live-stats-1200x630",
    label: "Live Stats · 1200×630",
    use: "Investor updates · LinkedIn announcements",
    w: 1200, h: 630,
    variant: "dashboard",
  },
  {
    id: "live-growth-1080x1080",
    label: "Growth Spotlight · 1080×1080",
    use: "IG feed · launch posts",
    w: 1080, h: 1080,
    variant: "growth",
  },
  {
    id: "live-platforms-1080x1920",
    label: "Social Reach · 1080×1920",
    use: "Stories · TikTok cover",
    w: 1080, h: 1920,
    variant: "platforms",
  },
];

export default function MarketingKit() {
  const [busyId, setBusyId] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get("/news/analytics?days=30");
      setAnalytics(data);
    } catch {
      // Silent fail — live tiles will render zeroed placeholder values
      setAnalytics({ totals: {}, daily: [], posts_by_platform: [], hours: [], trust: {} });
    } finally {
      setLoadingAnalytics(false);
    }
  };

  useEffect(() => { loadAnalytics(); }, []);

  const download = async (tileId) => {
    setBusyId(tileId);
    try {
      const node = document.getElementById(`tile-${tileId}`);
      if (!node) throw new Error("Tile not found");
      const dataUrl = await toPng(node, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0a0a0d",
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `broadcastflow-ai-${tileId}.png`;
      document.body.appendChild(a); a.click();
      setTimeout(() => a.remove(), 500);
      toast.success("Image downloaded");
    } catch (e) {
      toast.error(`Capture failed: ${e?.message?.slice(0, 200) || "unknown"}`);
    } finally { setBusyId(null); }
  };

  const downloadAll = async () => {
    for (const t of [...TILES, ...LIVE_TILES]) await download(t.id);
  };

  return (
    <div className="p-6 md:p-10 animate-fade-up max-w-7xl mx-auto" data-testid="marketing-kit-page">
      <PageHeader
        eyebrow="// MARKETING KIT"
        title="Press-ready branded visuals."
        description="Download high-resolution PNGs of every BroadcastFlow AI press-kit tile — perfect for investor decks, app-store listings, partnership pitches and social bios."
        action={
          <button
            onClick={downloadAll}
            disabled={busyId !== null}
            data-testid="mk-download-all"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 type-button-label text-black amber-glow disabled:opacity-50"
          >
            {busyId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Download all
          </button>
        }
      />

      {/* Embed-anywhere widget — copy-paste <script> tag that bands/sponsors
          can drop on their own sites to render this station's branded card */}
      <EmbedWidgetPanel />

      {/* "Now Spinning" live ticker — separate embed for partner sites */}
      <NowSpinningEmbedPanel />

      {/* Static tiles */}
      <div className="mt-8 grid md:grid-cols-2 gap-6" data-testid="mk-grid">
        {TILES.map((t) => (
          <TileCard key={t.id} t={t} busyId={busyId} onDownload={download}>
            <StaticTile t={t} />
          </TileCard>
        ))}
      </div>

      {/* Live dashboard tiles */}
      <div className="mt-10 mb-4 flex items-center justify-between">
        <div>
          <div className="font-mono type-status-badge uppercase tracking-[0.25em] text-cyan-300">// LIVE DASHBOARD TILES</div>
          <h3 className="type-section-heading text-ink-primary mt-1 flex items-center gap-2">
            <Activity className="h-5 w-5 text-cyan-300" /> Press visuals backed by real analytics
          </h3>
          <p className="type-body text-ink-muted mt-1">
            These tiles embed live KPI panels, growth charts and platform performance pulled from your station&apos;s last 30 days.
          </p>
        </div>
        <button
          onClick={loadAnalytics}
          disabled={loadingAnalytics}
          data-testid="mk-refresh-analytics"
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] hover:text-cyan-300 transition disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${loadingAnalytics ? "animate-spin" : ""}`} /> Refresh data
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6" data-testid="mk-live-grid">
        {LIVE_TILES.map((t) => (
          <TileCard key={t.id} t={t} busyId={busyId} onDownload={download} liveBadge>
            <LiveTile t={t} analytics={analytics} />
          </TileCard>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-line bg-bg-surface p-5 text-xs text-ink-muted">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-2">
          <Palette className="h-3 w-3" /> // BRAND GUIDELINES
        </div>
        <ul className="space-y-1 list-disc list-inside ml-1">
          <li><strong className="text-ink-primary">Primary orange</strong>: <code className="text-brand-amber">#FF8A1F</code> — the &ldquo;Flow&rdquo; wordmark + microphone</li>
          <li><strong className="text-ink-primary">Secondary cyan</strong>: <code className="text-cyan-400">#0093FF</code> — the &ldquo;AI&rdquo; wordmark + circuit lines</li>
          <li><strong className="text-ink-primary">Background</strong>: deep navy <code>#0F172A</code> or near-black <code>#0a0a0d</code></li>
          <li><strong className="text-ink-primary">Typography</strong>: Quicksand display + system mono for eyebrow labels</li>
        </ul>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// TileCard — outer card with header + preview + download button
// ──────────────────────────────────────────────────────────────
function TileCard({ t, busyId, onDownload, liveBadge, children }) {
  const wrapRef = useRef(null);

  // After children render, recompute scale so the full-size tile fits the preview box
  useEffect(() => {
    const el = document.getElementById(`tile-${t.id}`);
    const parent = el?.parentElement;
    if (el && parent) {
      const scale = parent.clientWidth / t.w;
      el.style.setProperty("--tile-scale", String(scale));
    }
  });

  return (
    <article className="rounded-2xl border border-line bg-bg-surface p-4" data-testid={`mk-card-${t.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">// {t.label}</div>
            {liveBadge && (
              <span className="font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                LIVE
              </span>
            )}
          </div>
          <div className="text-[11px] text-ink-muted mt-0.5">{t.use}</div>
        </div>
        <button
          onClick={() => onDownload(t.id)}
          disabled={busyId === t.id}
          data-testid={`mk-download-${t.id}`}
          className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-[11px] hover:text-cyan-300 transition disabled:opacity-50"
        >
          {busyId === t.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          PNG
        </button>
      </div>

      {/* Live tile preview — scaled to fit container width while preserving aspect ratio */}
      <div ref={wrapRef} className="relative overflow-hidden rounded-lg border border-line bg-black w-full" style={{ aspectRatio: `${t.w} / ${t.h}` }}>
        <div
          id={`tile-${t.id}`}
          style={{
            width: `${t.w}px`,
            height: `${t.h}px`,
            position: "absolute",
            top: 0, left: 0,
            transform: `scale(var(--tile-scale, 0.45))`,
            transformOrigin: "top left",
            background: "radial-gradient(1200px 600px at 80% 20%, rgba(255,138,31,0.18), transparent 60%), radial-gradient(900px 500px at 10% 90%, rgba(0,147,255,0.16), transparent 65%), #0a0a0d",
            color: "#F1F5F9",
            fontFamily: "'Quicksand', 'Inter', sans-serif",
            overflow: "hidden",
          }}
        >
          {children}
        </div>
      </div>
    </article>
  );
}

// ──────────────────────────────────────────────────────────────
// Static tile (existing marketing visuals)
// ──────────────────────────────────────────────────────────────
function StaticTile({ t }) {
  const isPortrait = t.h > t.w;
  return (
    <>
      {/* subtle grid bg */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />
      <div style={{ position: "absolute", inset: isPortrait ? "120px 80px" : "80px", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <div>
          <div style={{
            fontFamily: "monospace", fontSize: isPortrait ? "28px" : "20px",
            letterSpacing: "0.3em", color: "#0093FF", textTransform: "uppercase", marginBottom: "24px",
          }}>// BROADCAST AUTOMATION OS</div>
          <h2 style={{
            fontWeight: 900,
            fontSize: isPortrait ? "84px" : (t.w >= 1200 ? "78px" : "60px"),
            lineHeight: 1.02, letterSpacing: "-0.02em", maxWidth: isPortrait ? "900px" : "780px",
          }}>
            {t.headline.split("broadcast")[0]}
            <span style={{ color: "#FF8A1F" }}>
              {t.headline.includes("broadcast") ? "broadcast" + t.headline.split("broadcast")[1] : ""}
            </span>
          </h2>
          <p style={{
            marginTop: "32px",
            fontSize: isPortrait ? "32px" : "24px",
            color: "#94A3B8",
            maxWidth: isPortrait ? "880px" : "760px",
            lineHeight: 1.4,
          }}>{t.sub}</p>
        </div>
        <BrandFooter portrait={isPortrait} />
      </div>
      <CornerAccents portrait={isPortrait} />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Live tile — embeds analytics panels into the press visual
// ──────────────────────────────────────────────────────────────
function LiveTile({ t, analytics }) {
  const a = analytics || { totals: {}, daily: [], posts_by_platform: [], trust: {} };
  const tot = a.totals || {};
  const isPortrait = t.h > t.w;

  // Derived KPIs (mirror NewsAnalytics logic)
  const followers = tot.auto_approved || 0;
  const engagement = tot.auto_posts || 0;
  const reach = (tot.in_window || 0) * 6 + (tot.videos || 0) * 12 + (tot.anchor_mixes || 0) * 4;
  const perf = tot.in_window ? Math.round(((tot.auto_published || 0) + ((tot.by_status || {}).published || 0)) / tot.in_window * 100) : 0;

  return (
    <>
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
        backgroundSize: "60px 60px",
      }} />

      {/* Header eyebrow */}
      <div style={{
        position: "absolute",
        top: isPortrait ? 90 : 60,
        left: isPortrait ? 80 : 70,
        right: isPortrait ? 80 : 70,
      }}>
        <div style={{
          fontFamily: "monospace", fontSize: isPortrait ? "26px" : "18px",
          letterSpacing: "0.3em", color: "#0093FF", textTransform: "uppercase",
        }}>// LIVE · LAST 30 DAYS</div>
        <h2 style={{
          fontWeight: 900,
          fontSize: isPortrait ? "72px" : (t.variant === "dashboard" ? "56px" : "64px"),
          lineHeight: 1.02, letterSpacing: "-0.02em",
          marginTop: 12,
        }}>
          {t.variant === "dashboard" && <>Real <span style={{ color: "#FF8A1F" }}>broadcast</span> performance.</>}
          {t.variant === "growth"    && <>Growth on <span style={{ color: "#FF8A1F" }}>autopilot</span>.</>}
          {t.variant === "platforms" && <>Reach <span style={{ color: "#FF8A1F" }}>everywhere</span>.</>}
        </h2>
      </div>

      {/* Live data panel — variant specific */}
      <div style={{
        position: "absolute",
        top: isPortrait ? 360 : (t.variant === "dashboard" ? 230 : 240),
        left: isPortrait ? 80 : 70,
        right: isPortrait ? 80 : 70,
        bottom: isPortrait ? 240 : 170,
        display: "flex",
        flexDirection: "column",
        gap: isPortrait ? 32 : 22,
      }}>
        {t.variant === "dashboard" && (
          <>
            <KpiRow kpis={[
              { label: "Followers Δ",    big: `+${formatBig(followers)}`,  accent: "#10B981", Icon: TrendingUp },
              { label: "Reach",          big: formatBig(reach),             accent: "#0078D4", Icon: Eye },
              { label: "Engagement",     big: formatBig(engagement),        accent: "#8B5CF6", Icon: Heart },
              { label: "Publish rate",   big: `${perf}%`,                   accent: "#0093FF", Icon: Rocket },
            ]} />
            <GrowthChart daily={a.daily || []} h={130} />
          </>
        )}

        {t.variant === "growth" && (
          <>
            <div style={{ display: "flex", gap: 24 }}>
              <BigStat label="Stories live" big={formatBig(tot.in_window || 0)} accent="#FF8A1F" />
              <BigStat label="Videos rendered" big={formatBig(tot.videos || 0)} accent="#0093FF" />
            </div>
            <GrowthChart daily={a.daily || []} h={280} bigLabels />
          </>
        )}

        {t.variant === "platforms" && (
          <>
            <BigStat label="Total auto-posts (30d)" big={formatBig(engagement)} accent="#FF8A1F" big2 />
            <PlatformBars platforms={a.posts_by_platform || []} />
          </>
        )}
      </div>

      <BrandFooter portrait={isPortrait} absolute />
      <CornerAccents portrait={isPortrait} />
    </>
  );
}

// ──────────────────────────────────────────────────────────────
// Sub-components for live embedded panels (inline-styled for html-to-image)
// ──────────────────────────────────────────────────────────────
function KpiRow({ kpis }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 18 }}>
      {kpis.map((k) => (
        <div key={k.label} style={{
          background: "rgba(30,41,59,0.85)",
          border: "1px solid rgba(36,48,73,0.9)",
          borderRadius: 18,
          padding: "18px 20px",
          boxShadow: `0 8px 24px -10px ${k.accent}55`,
          position: "relative",
          overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", right: -30, top: -30, height: 120, width: 120,
            background: k.accent, opacity: 0.18, filter: "blur(40px)", borderRadius: "50%",
          }} />
          <div style={{
            height: 40, width: 40, borderRadius: 12,
            background: k.accent, display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", boxShadow: `0 6px 18px -4px ${k.accent}80`,
          }}>
            <k.Icon size={20} />
          </div>
          <div style={{
            fontFamily: "monospace", fontSize: 10, color: "#94A3B8",
            textTransform: "uppercase", letterSpacing: "0.18em", marginTop: 14,
          }}>{k.label}</div>
          <div style={{ fontWeight: 900, fontSize: 34, lineHeight: 1, marginTop: 4, color: "#F1F5F9" }}>{k.big}</div>
        </div>
      ))}
    </div>
  );
}

function BigStat({ label, big, accent, big2 }) {
  return (
    <div style={{
      background: "rgba(30,41,59,0.85)",
      border: "1px solid rgba(36,48,73,0.9)",
      borderRadius: 22,
      padding: "26px 30px",
      flex: 1,
      boxShadow: `0 12px 30px -12px ${accent}55`,
    }}>
      <div style={{
        fontFamily: "monospace", fontSize: 12, color: "#94A3B8",
        textTransform: "uppercase", letterSpacing: "0.22em",
      }}>{label}</div>
      <div style={{
        fontWeight: 900,
        fontSize: big2 ? 120 : 64,
        lineHeight: 1, marginTop: 8, color: accent,
      }}>{big}</div>
    </div>
  );
}

function GrowthChart({ daily, h = 160, bigLabels }) {
  const W = 1040, H = h, pad = 18;
  const n = daily.length;
  const max = Math.max(1, ...daily.map((d) => d.ingested || 0));
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - (v / max) * (H - 2 * pad);
  const buildPath = (key) => {
    if (n === 0) return "";
    const pts = daily.map((d, i) => [x(i), y(d[key] || 0)]);
    let path = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
      const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
      const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
      const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
    }
    return path;
  };
  const reachPath = buildPath("ingested");
  const reachArea = n > 0 ? reachPath + ` L ${x(n - 1)} ${H - pad} L ${x(0)} ${H - pad} Z` : "";
  const pubPath = buildPath("published");

  return (
    <div style={{
      background: "rgba(30,41,59,0.85)",
      border: "1px solid rgba(36,48,73,0.9)",
      borderRadius: 22,
      padding: "18px 22px",
      flex: 1,
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 10,
      }}>
        <div style={{
          fontFamily: "monospace", fontSize: bigLabels ? 14 : 11, color: "#0093FF",
          textTransform: "uppercase", letterSpacing: "0.22em",
        }}>// GROWTH CHART</div>
        <div style={{ display: "flex", gap: 14, fontSize: bigLabels ? 14 : 11, color: "#94A3B8" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 6, background: "#0078D4", borderRadius: 2, display: "inline-block" }} /> Reach
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 14, height: 6, background: "#8B5CF6", borderRadius: 2, display: "inline-block" }} /> Published
          </span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: H, display: "block" }}>
        <defs>
          <linearGradient id="reachGradMK" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0078D4" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#0078D4" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1={pad} x2={W - pad} y1={H - pad - p * (H - 2 * pad)} y2={H - pad - p * (H - 2 * pad)} stroke="#243049" strokeDasharray="4 4" />
        ))}
        {n > 0 && <>
          <path d={reachArea} fill="url(#reachGradMK)" />
          <path d={reachPath} fill="none" stroke="#0078D4" strokeWidth="3" />
          <path d={pubPath} fill="none" stroke="#8B5CF6" strokeWidth="3" />
        </>}
        {n === 0 && (
          <text x={W / 2} y={H / 2} textAnchor="middle" fill="#475569" fontFamily="monospace" fontSize="14">
            Awaiting newsroom data
          </text>
        )}
      </svg>
    </div>
  );
}

const PLATFORM_COLORS = {
  facebook: "#1877F2", instagram: "#E1306C", x: "#F1F5F9", twitter: "#F1F5F9",
  tiktok: "#25F4EE", youtube: "#FF0000", whatsapp: "#25D366",
};

function PlatformBars({ platforms }) {
  const rows = platforms.length > 0 ? platforms : [{ platform: "facebook", count: 0 }, { platform: "instagram", count: 0 }, { platform: "x", count: 0 }];
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={{
      background: "rgba(30,41,59,0.85)",
      border: "1px solid rgba(36,48,73,0.9)",
      borderRadius: 22,
      padding: "22px 26px",
      flex: 1,
      display: "flex", flexDirection: "column", gap: 18,
    }}>
      <div style={{
        fontFamily: "monospace", fontSize: 14, color: "#0093FF",
        textTransform: "uppercase", letterSpacing: "0.22em",
      }}>// SOCIAL PERFORMANCE</div>
      {rows.map((r) => {
        const color = PLATFORM_COLORS[(r.platform || "").toLowerCase()] || "#94A3B8";
        return (
          <div key={r.platform}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 22, color: "#F1F5F9", textTransform: "capitalize" }}>{r.platform}</span>
              <span style={{ fontFamily: "monospace", fontSize: 22, color }}>{r.count}</span>
            </div>
            <div style={{ height: 14, borderRadius: 8, background: "#0F172A", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(r.count / max) * 100}%`, background: color, borderRadius: 8 }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function BrandFooter({ portrait, absolute }) {
  const positionStyle = absolute ? {
    position: "absolute",
    bottom: portrait ? 120 : 64,
    left: portrait ? 80 : 70,
    right: portrait ? 80 : 70,
  } : {};
  return (
    <div style={{
      ...positionStyle,
      display: "flex", alignItems: "center", gap: 20,
    }}>
      <img
        src="/brand/broadcastflowai-hero.png"
        alt="BroadcastFlow AI"
        crossOrigin="anonymous"
        style={{ height: portrait ? 140 : 84, width: "auto", objectFit: "contain" }}
      />
      <div style={{ marginLeft: "auto", textAlign: "right" }}>
        <div style={{ fontSize: 16, color: "#94A3B8", letterSpacing: "0.15em", textTransform: "uppercase" }}>broadcastflowai.com</div>
        <div style={{ fontSize: 12, color: "#475569", marginTop: 4, letterSpacing: "0.1em" }}>© 2026 · MADE FOR BROADCASTERS</div>
      </div>
    </div>
  );
}

function CornerAccents({ portrait }) {
  return (
    <>
      <div style={{
        position: "absolute", top: 0, right: 0,
        width: portrait ? 240 : 180,
        height: portrait ? 240 : 180,
        background: "linear-gradient(135deg, transparent 50%, rgba(255,138,31,0.5))",
      }} />
      <div style={{
        position: "absolute", bottom: 0, left: 0,
        width: portrait ? 240 : 180,
        height: portrait ? 240 : 180,
        background: "linear-gradient(315deg, transparent 50%, rgba(0,147,255,0.4))",
      }} />
    </>
  );
}

function formatBig(n) {
  if (!n) return "0";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n);
}

/**
 * EmbedWidgetPanel — copy-paste embed snippet + live preview.
 * Bands, sponsors and partners drop the tag on their site → free organic
 * backlinks + listener referral funnel for the station.
 */
function EmbedWidgetPanel() {
  const [station, setStation] = useState(null);
  const [theme, setTheme] = useState("dark");
  const [compact, setCompact] = useState(false);
  const [showBlog, setShowBlog] = useState(false);
  const [showCustom, setShowCustom] = useState(true);
  const [copied, setCopied] = useState(false);
  const previewRef = useRef(null);

  useEffect(() => {
    api.get("/stations/me")
      .then((r) => setStation(r.data))
      .catch(() => {});
  }, []);

  // Re-inject the embed widget into the preview pane whenever any toggle flips
  useEffect(() => {
    if (!station?.station_id || !previewRef.current) return;
    const pane = previewRef.current;
    let cancelled = false;
    pane.innerHTML = "";              // clear previous render (covers StrictMode re-runs)
    const s = document.createElement("script");
    s.src = `${API_BASE}/embed/widget.js?_=${Date.now()}`;
    s.setAttribute("data-station", station.station_id);
    s.setAttribute("data-theme", theme);
    if (compact) s.setAttribute("data-compact", "true");
    if (showBlog) s.setAttribute("data-show-blog", "true");
    if (!showCustom) s.setAttribute("data-show-custom", "false");
    pane.appendChild(s);
    return () => {
      cancelled = true;
      // Belt + braces: nuke anything the IIFE rendered + remove the script tag
      pane.innerHTML = "";
    };
  }, [station, theme, compact, showBlog, showCustom]);

  if (!station?.station_id) return null;
  const sid = station.station_id;
  // Build the snippet string the user copies (uses the public ingress, NOT the dev base)
  // We expose it as window.location.origin so the snippet always reflects the
  // domain where the user actually views the dashboard.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const attrs = [
    `src="${origin}/api/embed/widget.js"`,
    `data-station="${sid}"`,
    `data-theme="${theme}"`,
    compact ? `data-compact="true"` : null,
    showBlog ? `data-show-blog="true"` : null,
    !showCustom ? `data-show-custom="false"` : null,
  ].filter(Boolean);
  const snippet = `<script ${attrs.join(" ")}></script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Embed snippet copied");
    } catch {
      toast.error("Couldn't copy — select the snippet manually");
    }
  };

  const mailto = `mailto:?subject=${encodeURIComponent("Embed " + station.name + " on your site")}&body=${encodeURIComponent(
    "Paste this single line of code into any page on your site to instantly display " + station.name + "'s logo, listen-live button, social links and more. It updates automatically when we change anything on our end:\n\n" + snippet + "\n\nThanks!"
  )}`;

  return (
    <div className="mt-8 rounded-xl border border-line bg-bg-surface p-6" data-testid="embed-widget-panel">
      <div className="flex items-center justify-between mb-1 gap-3">
        <div className="flex items-center gap-2">
          <Code2 className="h-4 w-4 text-cyan-300" />
          <h3 className="type-section-heading text-ink-primary">Embed-anywhere widget</h3>
        </div>
        <span className="font-mono type-status-badge uppercase tracking-[0.2em] text-cyan-300">// SHARE YOUR STATION</span>
      </div>
      <p className="type-body text-ink-secondary mb-5">
        Send this one-line snippet to bands you feature, sponsors, merch partners or your own fan blog. They paste it once — it renders {station.name}&apos;s branded card with logo, listen-live button, every social link and every custom link. Free organic backlinks. Updates automatically when you change anything in your Station settings.
      </p>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
        {/* Controls + snippet */}
        <div className="space-y-4">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-2">// CUSTOMIZE</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <ThemeBtn label="Dark" active={theme === "dark"} onClick={() => setTheme("dark")} testid="embed-theme-dark" />
              <ThemeBtn label="Light" active={theme === "light"} onClick={() => setTheme("light")} testid="embed-theme-light" />
            </div>
            <div className="mt-3 space-y-1.5">
              <Toggle label="Compact (sidebar size)" checked={compact} onChange={setCompact} testid="embed-compact" />
              <Toggle label="Show 3 latest news stories" checked={showBlog} onChange={setShowBlog} testid="embed-show-blog" />
              <Toggle label="Show custom links (Patreon, Discord, etc.)" checked={showCustom} onChange={setShowCustom} testid="embed-show-custom" />
            </div>
          </div>

          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-2">// COPY-PASTE SNIPPET</div>
            <div className="relative">
              <pre className="rounded-lg border border-line bg-bg p-3 text-[11px] font-mono text-cyan-200 whitespace-pre-wrap break-all leading-relaxed" data-testid="embed-snippet">
                {snippet}
              </pre>
              <button
                onClick={copy}
                data-testid="embed-copy"
                className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-cyan-500/40 bg-cyan-500/10 px-2 py-1 text-[10px] text-cyan-200 hover:text-cyan-100 hover:border-cyan-400 transition"
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mt-2 text-[10px] text-ink-muted">
              Drop this anywhere inside the <code className="text-cyan-300">&lt;body&gt;</code> of any HTML page —
              WordPress sidebar, Squarespace block, Bandcamp custom HTML, a band&apos;s personal site, etc.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <a
              href={mailto}
              data-testid="embed-email"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-cyan-300 transition"
            ><ExternalLink className="h-3 w-3" /> Email snippet to a partner</a>
            <a
              href={`${origin}/api/embed/station/${sid}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="embed-json"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-bg-elevated px-3 py-1.5 text-xs hover:text-cyan-300 transition"
            ><ExternalLink className="h-3 w-3" /> View raw JSON</a>
          </div>
        </div>

        {/* Live preview */}
        <div>
          <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-ink-muted mb-2">// LIVE PREVIEW</div>
          <div
            className="rounded-lg border border-line bg-bg p-4 min-h-[260px] flex items-start justify-center"
            data-testid="embed-preview"
          >
            <div ref={previewRef} className="w-full" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ThemeBtn({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`rounded-md border px-3 py-2 font-mono uppercase tracking-wider text-[10px] transition ${
        active
          ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-100"
          : "border-line bg-bg-elevated text-ink-secondary hover:text-cyan-300"
      }`}
    >{label}</button>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-ink-secondary cursor-pointer">
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="accent-cyan-400"
      />
      {label}
    </label>
  );
}


/* ─────────────────────────────────────────────────────────────
 * NowSpinningEmbedPanel — copy-paste <script> snippet that drops a
 * live "Now Spinning" card on any partner site. Free promo + traffic
 * back to the station's stream.
 * ───────────────────────────────────────────────────────────── */
function NowSpinningEmbedPanel() {
  const [stationId, setStationId] = useState("");
  const [theme, setTheme] = useState("dark");
  const [variant, setVariant] = useState("card");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/stations/me");
        setStationId(r.data?.station_id || "");
      } catch { /* ignore */ }
    })();
  }, []);

  const snippet = `<script src="${API_BASE.replace(/\/api$/, "")}/api/embed/nowspinning.js"
        data-station="${stationId}"
        data-theme="${theme}"
        data-variant="${variant}"
        async></script>`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      toast.success("Now Spinning snippet copied!");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Could not copy"); }
  };

  return (
    <section className="mt-8 rounded-2xl border border-line bg-bg-surface p-6" data-testid="now-spinning-embed-panel">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Radio className="h-5 w-5 text-rose-400" />
          <h2 className="type-section-heading text-ink-primary">&ldquo;Now Spinning&rdquo; embed</h2>
        </div>
        <span className="type-status-badge font-mono uppercase tracking-wider text-ink-muted">
          updates every 30s · no auth required
        </span>
      </div>
      <p className="type-body text-ink-secondary mb-4">
        Drop this one-line snippet into any partner site, fan page, or sponsor&apos;s footer. Visitors see a live ticker of what&apos;s playing on your station, with a Listen Live button that drives traffic back to your stream.
      </p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">Live preview</div>
          {stationId ? (
            <NowSpinning stationId={stationId} variant={variant} />
          ) : (
            <div className="text-sm text-ink-muted">Loading...</div>
          )}
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">Options</div>
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-secondary w-20">Variant</span>
              {["card", "ticker"].map((v) => (
                <button
                  key={v}
                  onClick={() => setVariant(v)}
                  data-testid={`ns-embed-variant-${v}`}
                  className={`px-3 py-1 rounded-md border text-xs font-mono uppercase tracking-wider transition ${
                    variant === v ? "border-brand-amber text-brand-amber bg-brand-amber/10" : "border-line text-ink-muted hover:text-ink-primary"
                  }`}
                >{v}</button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-ink-secondary w-20">Theme</span>
              {["dark", "light"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTheme(t)}
                  data-testid={`ns-embed-theme-${t}`}
                  className={`px-3 py-1 rounded-md border text-xs font-mono uppercase tracking-wider transition ${
                    theme === t ? "border-brand-amber text-brand-amber bg-brand-amber/10" : "border-line text-ink-muted hover:text-ink-primary"
                  }`}
                >{t}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-md border border-line bg-bg-elevated p-3" data-testid="ns-embed-snippet">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Embed code</span>
          <button
            onClick={copy}
            disabled={!stationId}
            data-testid="ns-embed-copy-btn"
            className="text-xs inline-flex items-center gap-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber px-3 py-1 hover:bg-brand-amber/20 transition disabled:opacity-50"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        <pre className="text-[11px] font-mono text-ink-primary whitespace-pre-wrap break-all">{snippet}</pre>
      </div>

      <div className="mt-3 text-[11px] text-ink-muted">
        Partners can also use <code className="font-mono text-ink-secondary">data-poll-ms=&quot;60000&quot;</code> to slow polling, or call the JSON endpoint directly at <code className="font-mono text-ink-secondary">/api/public/nowplaying/{stationId}</code>.
      </div>
    </section>
  );
}
